import { ChainData, Token } from "@0xsquid/squid-types";
import { useMemo } from "react";
import { formatBNToReadable, parseToBigInt } from "../../../services";
import {
  calculateMinAmountValueWarnMsg,
  checkIsGasBalanceEnough,
} from "../../../services/internal/estimateService";
import { useSourceChainGasToken } from "../../tokens/useSourceChainGasToken";
import { useSendTransactionGas } from "./useSendTransactionGas";

interface UseEstimateSendReturnType {
  /**
   * Estimated gas for the transaction
   */
  estimatedGas: bigint | undefined;
  isLoading: boolean;
  /**
   * Whether the gas token balance is enough to pay for the network gas fees
   */
  isGasBalanceEnough: boolean;
  /**
   * Whether the balance of the selected token is enough to send the requested `amount`.
   */
  isBalanceEnough: boolean;
  /**
   * The safe amount the user can send to have enough balance to pay for the network gas fees
   */
  minAmountValueWarnMsg: string | undefined;
  /**
   * Whether the selected token pays the network fee
   */
  tokenPaysGasFees: boolean;
  /**
   * The balance of the gas token needed
   * including source amount if the source token is the gas token
   */
  gasBalanceNeeded: string | undefined;
}

export function useEstimateSendTransaction({
  chain,
  token,
  amount,
  balance,
  from,
}: {
  chain: ChainData | undefined;
  token: Token | undefined;
  amount: string | undefined;
  balance: string | undefined;
  from: string | undefined;
}): UseEstimateSendReturnType {
  const { data: estimatedGas = BigInt(0), isLoading } = useSendTransactionGas({
    chain,
    token,
    from,
  });

  const { chainFeeParams, gasToken } = useSourceChainGasToken({
    fromChain: chain,
    fromToken: token,
  });

  const isGasBalanceEnough = useMemo(() => {
    if (!amount || !token?.decimals) return false;

    return checkIsGasBalanceEnough({
      fromAmount: parseToBigInt(amount, token.decimals).toString(),
      networkFeesWei: estimatedGas,
      chainFeeParams,
    });
  }, [amount, estimatedGas, token, chainFeeParams]);

  const gasBalanceNeeded = useMemo(() => {
    if (!amount || !token?.decimals || !gasToken?.decimals) return undefined;

    // gas fees + fromAmount (if fromToken is gas token)
    const totalGasBalanceNeeded =
      estimatedGas +
      parseToBigInt(
        chainFeeParams?.fromTokenPaysGasFees ? (amount ?? "0") : "0",
        token.decimals
      );

    return formatBNToReadable(totalGasBalanceNeeded, gasToken.decimals);
  }, [
    amount,
    chainFeeParams?.fromTokenPaysGasFees,
    estimatedGas,
    gasToken?.decimals,
    token?.decimals,
  ]);

  const isBalanceEnough = useMemo(() => {
    if (token?.decimals == null || !balance || !amount) return false;

    return (
      parseToBigInt(balance, token.decimals) >=
      parseToBigInt(amount, token.decimals)
    );
  }, [amount, balance, token?.decimals]);

  const minAmountValueWarnMsg = useMemo((): string | undefined => {
    if (!token?.address || !estimatedGas || !chainFeeParams) return undefined;

    return calculateMinAmountValueWarnMsg({
      chainFeeParams,
      networkFeesWei: estimatedGas,
      fromTokenDecimals: token.decimals,
    });
  }, [estimatedGas, token, chainFeeParams]);

  return {
    estimatedGas,
    gasBalanceNeeded,
    isBalanceEnough,
    tokenPaysGasFees: chainFeeParams?.fromTokenPaysGasFees ?? false,
    isLoading,
    isGasBalanceEnough,
    minAmountValueWarnMsg,
  };
}
